import { styled } from '@mui/material';
import { useIsExploreUser } from 'hooks/client';
import { INDICATOR_TAG_TYPES } from 'modules/decision/constants';
import { useSettingsDispatchContext } from 'modules/decision/pages/Explore/components/Contexts/SettingsContext/SettingsContext';
import { useApplyIndicator } from 'modules/decision/pages/Explore/hooks/applyIndicator';
import type { LibraryIndicatorT } from 'modules/decision/types/indicators';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';

import { getIndicatorTags, isCityIndicator } from '../helpers';
import IndicatorActions from './IndicatorActions';
import IndicatorCardPaper from './IndicatorCardPaper';
import IndicatorFields from './IndicatorFields';
import IndicatorCardHeader from './IndicatorHeader';
import IndicatorTag from './IndicatorTag';

const StyPreviewImage = styled('img')({
    objectFit: 'contain',
    height: 180,
    width: '100%',
});

type Props = {
    indicator: LibraryIndicatorT;
    fullWidth?: boolean;
};

const IndicatorCardExplore = ({ indicator, fullWidth = false }: Props) => {
    const { t } = useTranslation();
    const isExploreUser = useIsExploreUser();
    const applyIndicator = useApplyIndicator();
    const { updateVizSettings } = useSettingsDispatchContext();
    const isCity = isCityIndicator(indicator);

    const handleUseAsModel = () => {
        applyIndicator(indicator);
        updateVizSettings({
            title: indicator.title,
            vizType: indicator.vizType,
            settings: indicator.settings,
        });
    };

    const indicatorTags = getIndicatorTags(indicator, t);
    const chipTags = indicatorTags.filter(
        (tag) =>
            tag.type === INDICATOR_TAG_TYPES.connector ||
            tag.type === INDICATOR_TAG_TYPES.title,
    );

    return (
        <IndicatorCardPaper fullWidth={fullWidth}>
            <Flex direction="column" gap={1}>
                <IndicatorCardHeader
                    isInExplore
                    indicator={indicator}
                    isExploreUser={isExploreUser}
                />
                {isCity ? (
                    chipTags.length > 0 && (
                        <Flex wrap="wrap" gap={1}>
                            {chipTags.map((tag) => (
                                <IndicatorTag
                                    key={`chip-${tag.value}`}
                                    indicatorTag={tag}
                                />
                            ))}
                        </Flex>
                    )
                ) : (
                    <StyPreviewImage
                        alt="Indicator preview image"
                        src={indicator.preview}
                    />
                )}
                <IndicatorFields indicatorTags={indicatorTags} />
            </Flex>
            <IndicatorActions
                isInExplore
                indicator={indicator}
                isExploreUser={isExploreUser}
                showCopyButton={false}
                onUseAsModel={handleUseAsModel}
            />
        </IndicatorCardPaper>
    );
};

export default IndicatorCardExplore;
